import { QueryResultRow } from '@kentik/ui-shared/query/query.types';
import { DimensionFormatter } from 'app/views/ude/dataviews/util/highcharts';
import { AxisOptions, Options } from 'highcharts';
import { DefaultTheme } from 'styled-components';

/**
 * Format a row's dimensions into a display string for the series name.
 * Prefers enrichedId parts (e.g. device name instead of device ID) when available,
 * and still applies the dimension formatter for values like agent IDs.
 */
export function formatRowName(
  row: QueryResultRow,
  dimensions: { name: string }[],
  formatter?: DimensionFormatter
): string {
  // enrichedId has the same dimension order as row.id (and query.dimensions),
  // so we can split it by ' ---- ' and use its parts as enriched display values.
  const enrichedParts = row.enrichedId ? row.enrichedId.split(' ---- ') : null;

  return dimensions
    .map((dim, idx) => {
      const value =
        (enrichedParts ? (enrichedParts[idx] ?? row.dimensions[dim.name]) : row.dimensions[dim.name]) || '---';
      return formatter ? formatter(dim.name, value) : value;
    })
    .join(' - ');
}

export function getBaseChartStyle(theme: DefaultTheme): Options {
  const axisOptions: AxisOptions = {
    lineColor: theme.colors.chart.axisLine,
    tickColor: theme.colors.chart.axisTick,
    gridLineColor: theme.colors.chart.gridLine,
    gridLineWidth: 1,
    title: {
      style: {
        color: theme.colors.muted
      }
    },
    labels: {
      style: {
        color: theme.colors.muted
      }
    }
  };

  return {
    chart: {
      backgroundColor: 'transparent',
      style: {
        fontFamily: theme.fonts.body
      }
    },
    credits: { enabled: false },
    title: { text: undefined },
    legend: {
      itemStyle: {
        color: theme.colors.chart.legendLabels
      },
      itemHoverStyle: {
        color: theme.colors.primary
      },
      itemHiddenStyle: {
        color: theme.colors.chart.legendHiddenLabels
      }
    },
    plotOptions: {
      series: {
        animation: { duration: 350 },
        marker: {
          enabled: false,
          radius: 2,
          symbol: 'circle'
        }
      }
    },
    tooltip: {
      followPointer: true,
      outside: true,
      shared: true,
      backgroundColor: theme.colors.chart.tooltipBackground,
      style: {
        color: theme.colors.body,
        fontSize: '13px'
      },
      shadow: {
        opacity: 0.1,
        width: 3,
        offsetX: 1,
        offsetY: 1
      }
    },
    xAxis: {
      ...(axisOptions as Highcharts.XAxisOptions)
    },
    yAxis: {
      ...(axisOptions as Highcharts.YAxisOptions),
      softMin: 0
    }
  };
}
